import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Palette, Layout, Type, Eye, Save } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/uiDir/select";
import { Slider } from "@/components/uiDir/slider";
import { Switch } from "@/components/uiDir/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/uiDir/tabs";
import { toast } from "sonner";

export default function CustomizeTheme() {
  const [theme, setTheme] = useState({
    template: "minimal",
    primaryColor: "#3B82F6",
    accentColor: "#EC4899",
    layout: "grid-3",
    fontHeading: "space-grotesk",
    fontBody: "inter",
    borderRadius: 12,
    darkMode: false,
  });

  const templates = [
    { value: "minimal", label: "Minimal", description: "Clean and simple" },
    { value: "creative", label: "Creative", description: "Bold and colorful" },
    { value: "professional", label: "Professional", description: "Corporate style" },
    { value: "dark", label: "Dark Mode", description: "OLED-friendly" },
  ];

  const handleSave = () => {
    toast.success("Theme saved successfully!");
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-7xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Customize Theme</h1>
          <p className="text-muted-foreground">Make your portfolio uniquely yours</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Tabs defaultValue="template" className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
                <TabsTrigger value="template">
                  <Layout className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="colors">
                  <Palette className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="typography">
                  <Type className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="layout">Layout</TabsTrigger>
              </TabsList>

              <TabsContent value="template" className="space-y-4">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Theme Templates</CardTitle>
                    <CardDescription>Start with a pre-designed template</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {templates.map((template) => (
                      <div
                        key={template.value}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary ${
                          theme.template === template.value ? "border-primary bg-primary/5" : "border-border"
                        }`}
                        onClick={() => setTheme({ ...theme, template: template.value })}
                      >
                        <h4 className="font-semibold mb-1">{template.label}</h4>
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="colors" className="space-y-4">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Color Palette</CardTitle>
                    <CardDescription>Customize your brand colors</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={theme.primaryColor}
                          onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                          className="h-12 w-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={theme.primaryColor}
                          onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Accent Color</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={theme.accentColor}
                          onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                          className="h-12 w-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={theme.accentColor}
                          onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Dark Mode</Label>
                        <p className="text-sm text-muted-foreground">Enable dark theme</p>
                      </div>
                      <Switch
                        checked={theme.darkMode}
                        onCheckedChange={(checked) => setTheme({ ...theme, darkMode: checked })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="typography" className="space-y-4">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Typography</CardTitle>
                    <CardDescription>Choose your fonts</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Heading Font</Label>
                      <Select value={theme.fontHeading} onValueChange={(value) => setTheme({ ...theme, fontHeading: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="space-grotesk">Space Grotesk</SelectItem>
                          <SelectItem value="inter">Inter</SelectItem>
                          <SelectItem value="poppins">Poppins</SelectItem>
                          <SelectItem value="playfair">Playfair Display</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Body Font</Label>
                      <Select value={theme.fontBody} onValueChange={(value) => setTheme({ ...theme, fontBody: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inter">Inter</SelectItem>
                          <SelectItem value="roboto">Roboto</SelectItem>
                          <SelectItem value="open-sans">Open Sans</SelectItem>
                          <SelectItem value="lora">Lora</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Border Radius: {theme.borderRadius}px</Label>
                      <Slider
                        value={[theme.borderRadius]}
                        onValueChange={(value) => setTheme({ ...theme, borderRadius: value[0] })}
                        min={0}
                        max={24}
                        step={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="layout" className="space-y-4">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Layout Options</CardTitle>
                    <CardDescription>Configure your portfolio layout</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Project Grid</Label>
                      <Select value={theme.layout} onValueChange={(value) => setTheme({ ...theme, layout: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="grid-2">2 Columns</SelectItem>
                          <SelectItem value="grid-3">3 Columns</SelectItem>
                          <SelectItem value="masonry">Masonry</SelectItem>
                          <SelectItem value="list">List View</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex gap-4">
              <Button onClick={handleSave} className="flex-1 bg-gradient-primary">
                <Save className="mr-2 h-4 w-4" />
                Save Theme
              </Button>
              <Button variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>
            </div>
          </div>

          <div className="lg:sticky lg:top-8 h-fit">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
                <CardDescription>See your changes in real-time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-border rounded-lg p-6 bg-background min-h-[600px]">
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 rounded-full bg-gradient-primary mx-auto mb-4"></div>
                    <h2 className="text-2xl font-heading font-bold mb-2">John Doe</h2>
                    <p className="text-muted-foreground">Full-Stack Developer</p>
                  </div>
                  
                  <div className={`grid ${theme.layout === 'grid-3' ? 'grid-cols-3' : theme.layout === 'grid-2' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="aspect-square rounded-lg bg-muted"></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
